// SPDX-License-Identifier: AGPL-3.0-or-later

import type {VoiceParticipantAvatarEntry} from '@app/features/voice/components/VoiceParticipantAvatarList';
import {buildVoiceParticipantIdentity} from '@app/features/voice/utils/VoiceParticipantIdentity';

// stage: the TV part of the room stage a bot runs; identity is its host's (stage_<bot id>), name the bot's.
export type TvSource =
	| {kind: 'stage'; identity: string; name: string}
	| {kind: 'screen'; identity: string}
	| {kind: 'camera'; identity: string}
	| {kind: 'link'; identity: string};

const NO_LINKS: ReadonlySet<string> = new Set();

const identityOf = (entry: VoiceParticipantAvatarEntry): string =>
	buildVoiceParticipantIdentity(entry.userId, entry.connectionId);

// What this viewer may put on the TV: other people's screen shares first, then the room stage's board or video, then
// the video links played in the room (mine too: everyone watches it together), then other people's cameras. links: the
// identities whose video may show; stage: the stage's TV part, when it has one.
export function tvSources(
	entries: ReadonlyArray<VoiceParticipantAvatarEntry>,
	myUserId: string | null,
	links: ReadonlySet<string> = NO_LINKS,
	stage: {identity: string; name: string} | null = null,
): ReadonlyArray<TvSource> {
	const others = entries.filter((entry) => !entry.isLocal && entry.userId !== myUserId);
	return [
		...others
			.filter((entry) => entry.hasScreenShare)
			.map((entry) => ({kind: 'screen' as const, identity: identityOf(entry)})),
		...(stage ? [{kind: 'stage' as const, ...stage}] : []),
		...entries
			.filter((entry) => links.has(identityOf(entry)))
			.map((entry) => ({kind: 'link' as const, identity: identityOf(entry)})),
		...others
			.filter((entry) => entry.hasCamera)
			.map((entry) => ({kind: 'camera' as const, identity: identityOf(entry)})),
	];
}

// The first screen share takes the TV, as before (a stage written later never stops a watch); without one the stage
// the room's bot runs, then the first video link. Cameras go there only when picked.
export function defaultTvSource(sources: ReadonlyArray<TvSource>): TvSource | null {
	return (
		sources.find((source) => source.kind === 'screen') ??
		sources.find((source) => source.kind === 'stage') ??
		sources.find((source) => source.kind === 'link') ??
		null
	);
}
